/* eslint-disable perfectionist/sort-imports -- import order here defines the CSS cascade and plugin init order */
// Vue 3: Use createApp instead of new Vue
import axios from "axios"
import App from "./App.vue"
import router from "./router"
import store from "./store"
import i18n from "@/libs/i18n"

// Performance debugging (only in dev)
import {
  markAppMounted,
  markCheckpoint,
  measureSync,
  startPerformanceTracking,
} from "@/utils/performance-debug"

// Components
import DatePicker from "vue-datepicker-next"
import "vue-datepicker-next/index.css"
import "flatpickr/dist/flatpickr.css"
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css"
import CustomNumberInput from "@/views/components/CustomNumberInput.vue"

// Plugins
import MyPlugin from "@/plugins/error/error.plugins"
import Toast, { toastOptions } from "@/libs/toastification"
import VueSweetalert2 from "@/libs/sweet-alerts"
import VueClipboard from "@/libs/clipboard"
import VueTour from "@/libs/tour"
import aclPlugin from "@/libs/acl"
import axiosIns from "@/libs/axios"

// Bootstrap Vue Next (Vue 3)
import { BootstrapVueNext } from "bootstrap-vue-next"
import "bootstrap/dist/css/bootstrap.css"
import "bootstrap-vue-next/dist/bootstrap-vue-next.css"

// Maska (replaces v-mask for Vue 3)
import { vMaska } from "maska"
import { mask as vMaskLegacy } from "@/compat/v-mask-shim"

// Currency filter (Vue 3 compatibility shim)
import VueCurrencyFilter from "@/compat/vue-currency-filter-shim"

// PWA Service Worker
import wb from "./registerServiceWorker"

// Import core styles
import "@core/scss/core.scss"

// Import assets styles
import "@/assets/scss/style.scss"

// Import API service
import ApiService from "@/services/api.service"

// Start performance tracking
if (import.meta.env.DEV) {
  startPerformanceTracking()
}

// Create Vue 3 app instance
const app = createApp(App)

// Suppress harmless "parentNode" errors from bootstrap-vue compat components
// that manipulate DOM directly during unmount (modals, tooltips, popovers).
// These occur because bootstrap-vue removes DOM nodes before Vue's cleanup runs.
app.config.errorHandler = (err, instance, info) => {
  if (err instanceof TypeError && err.message.includes("reading 'parentNode'")) {
    return
  }
  console.error(err)
}

// Vue 3: Global properties (replaces Vue.prototype)
app.config.globalProperties.$workbox = wb
app.config.globalProperties.$userInfo = localStorage.getItem("user_info")
  ? JSON.parse(localStorage.getItem("user_info"))
  : { nationalCurrencyId: 76 }
app.config.globalProperties.$http = axiosIns

// Vue 3: Use plugins with app.use()
// Measure plugin registration time in dev
if (import.meta.env.DEV) {
  measureSync("router-setup", () => {
    app.use(router)
  })
  markCheckpoint("router-ready")

  measureSync("store-setup", () => {
    app.use(store)
  })
  markCheckpoint("store-ready")

  measureSync("i18n-setup", () => {
    app.use(i18n)
  })
  markCheckpoint("i18n-ready")

  measureSync("other-plugins-setup", () => {
    app.use(MyPlugin)
    app.use(Toast, toastOptions)
    app.use(VueSweetalert2)
    app.use(VueClipboard)
    app.use(VueTour)
    app.use(aclPlugin)
    app.use(BootstrapVueNext)
  })

  markCheckpoint("plugins-ready")
} else {
  app.use(router)
  app.use(store)
  app.use(i18n)
  app.use(MyPlugin)
  app.use(Toast, toastOptions)
  app.use(VueSweetalert2)
  app.use(VueClipboard)
  app.use(VueTour)
  app.use(aclPlugin)
  app.use(BootstrapVueNext)
}

// Currency filter plugin
app.use(VueCurrencyFilter, {
  symbol: "$",
  thousandsSeparator: ".",
  fractionCount: 2,
  fractionSeparator: ",",
  symbolPosition: "front",
  symbolSpacing: true,
  avoidEmptyDecimals: undefined,
})

const findOptionFromReducedValue = vSelect.methods.findOptionFromReducedValue
vSelect.methods.findOptionFromReducedValue = function (value) {
  const option = findOptionFromReducedValue.call(this, value)
  return option === value && value === 0 ? null : option
}

document.addEventListener("dblclick", (e) => {
  if (e.target.closest("a, button, input, select, textarea, label, .v-select, .dropdown-menu"))
    return
  const eye = e.target.closest(".table > tbody > tr")?.querySelector(".lucide-eye")
  const action = eye?.closest("a, button") ?? eye
  action?.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }))
})

const rowActionFallbackLabels = [
  [".lucide-eye", "View"],
  [".lucide-square-pen, .lucide-pen, .lucide-pen-line", "Edit"],
  [".lucide-trash, .lucide-trash-2", "Delete"],
  [".lucide-printer", "Print"],
]
let rowMenu = null
function closeRowMenu() {
  rowMenu?.remove()
  rowMenu = null
}

function rowActionLabel(svg, el) {
  for (let n = svg; n; n = n === el ? null : n.parentElement) {
    const tip = n.$__state?.value
    const text = tip?.title || tip?.content || n.getAttribute("title") || n.getAttribute("data-original-title")
    if (text)
      return text
  }
  const fallback = rowActionFallbackLabels.find(([selector]) => svg.matches(selector))
  return el.textContent.trim() || (fallback ? i18n.global.t(fallback[1]) : "")
}

document.addEventListener("contextmenu", (e) => {
  closeRowMenu()
  const row = e.target.closest(".table > tbody > tr")
  const native = "input, select, textarea, .v-select, a[href]:not([href=\"#\"])"
  if (!row || window.getSelection().toString() || e.target.closest(native))
    return

  const actions = [...row.querySelectorAll("svg.lucide")].flatMap((svg) => {
    const el = svg.closest("a, button")
    const usable
      = el
        && row.contains(el)
        && svg.closest(".table > tbody > tr") === row
        && svg.getClientRects().length
        && !el.disabled
        && !el.classList.contains("disabled")
        && el.getAttribute("aria-disabled") !== "true"
    return usable ? [{ svg, label: rowActionLabel(svg, el) }] : []
  })
  if (!actions.length)
    return
  e.preventDefault()

  rowMenu = document.createElement("div")
  rowMenu.className = "dropdown-menu show"
  rowMenu.setAttribute("role", "menu")
  rowMenu.style.cssText = "position: fixed; z-index: 2000; transform: none"
  for (const { svg, label } of actions) {
    const item = document.createElement("button")
    item.type = "button"
    item.className = "dropdown-item d-flex align-items-center gap-50"
    item.setAttribute("role", "menuitem")
    item.append(svg.cloneNode(true), label)
    item.addEventListener("click", () => {
      closeRowMenu()
      svg.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }))
    })
    rowMenu.append(item)
  }
  document.body.append(rowMenu)
  rowMenu.style.left = `${Math.max(0, Math.min(e.clientX, window.innerWidth - rowMenu.offsetWidth - 4))}px`
  rowMenu.style.top = `${Math.max(0, Math.min(e.clientY, window.innerHeight - rowMenu.offsetHeight - 4))}px`
})
document.addEventListener("click", e => rowMenu?.contains(e.target) || closeRowMenu())
document.addEventListener("keydown", e => e.key === "Escape" && closeRowMenu())
window.addEventListener("scroll", closeRowMenu, true)
window.addEventListener("resize", closeRowMenu)

let tableDrag = null
const stopClick = e => e.stopPropagation()
function endTableDrag() {
  if (tableDrag?.moved) {
    document.body.classList.remove("table-dragging")
    document.addEventListener("click", stopClick, true)
    setTimeout(() => document.removeEventListener("click", stopClick, true))
  }
  tableDrag = null
}

document.addEventListener("mousedown", (e) => {
  if (e.button !== 0 || e.altKey)
    return
  const table = e.target.closest(".table")
  if (!table || e.target.closest("a, button, input, select, textarea, label, .v-select, .dropdown-menu"))
    return
  let box = table.parentElement
  while (box && !(box.scrollWidth > box.clientWidth && /auto|scroll/.test(getComputedStyle(box).overflowX))) {
    box = box.parentElement
  }
  if (!box)
    return
  e.preventDefault()
  tableDrag = { box, x: e.clientX, left: box.scrollLeft, moved: false }
})
document.addEventListener("mousemove", (e) => {
  if (!tableDrag)
    return
  if (!(e.buttons & 1))
    return endTableDrag()
  const dx = e.clientX - tableDrag.x
  if (!tableDrag.moved && Math.abs(dx) < 5)
    return
  tableDrag.moved = true
  document.body.classList.add("table-dragging")
  tableDrag.box.scrollLeft = tableDrag.left - dx
})
document.addEventListener("mouseup", endTableDrag)

let focusedRow = null
function focusRow(tr) {
  focusedRow?.classList.remove("row-focused")
  rowFocusObserver.disconnect()
  focusedRow = tr
  if (!tr)
    return
  tr.classList.add("row-focused")
  rowFocusObserver.observe(tr.parentElement, { subtree: true, characterData: true })
}
const rowFocusObserver = new MutationObserver(() => focusRow(null))

document.addEventListener("click", (e) => {
  const tr = e.target.closest(".table > tbody > tr:not(.b-table-empty-slot, .b-table-busy-slot)")
  if (tr)
    focusRow(tr)
})

// Vue 3: Register global components with app.component()
app.component("VSelect", vSelect)
app.component("CustomNumberInput", CustomNumberInput)
app.component("DatePicker", DatePicker)
app.component("DatePicker", DatePicker)

// Register maska directive globally (replaces v-mask)
app.directive("maska", vMaska)
// Also register as v-mask for backward compatibility
app.directive("mask", vMaskLegacy)

// Configure axios defaults
axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL

const token = localStorage.getItem("access_token")
if (token) {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`
}

// Window onload handler
window.onload = () => {
  // Use mutations instead of direct state mutation
  const unreadDocCount = localStorage.getItem("UnreadDocCount")
    ? JSON.parse(localStorage.getItem("UnreadDocCount"))
    : {}
  store.commit("SET_UNREAD_DOC_COUNT", unreadDocCount)

  const userInfo = localStorage.getItem("user_info")
    ? JSON.parse(localStorage.getItem("user_info"))
    : null
  const enableBanknote = userInfo?.enableBanknoteInClientSide || false
  store.commit("SET_ENABLE_BANKNOTE_IN_CLIENT_SIDE", enableBanknote)
}

// Mount API interceptor
ApiService.mount401Interceptor()

// Vue 3: Mount the app
if (import.meta.env.DEV) {
  // Use nextTick to measure mount time more accurately
  measureSync("app-mount", () => {
    app.mount("#app")
  })
  // Mark as mounted after a microtask to ensure DOM is ready
  Promise.resolve().then(() => {
    markAppMounted()
  })
} else {
  app.mount("#app")
}
